// ProductsForecastPanel.jsx — sales forecast for individual products.
//
// Three horizons (day / week / month) × top-N products. Shows confidence%,
// model accuracy% (backtested), and a top-products table the branch
// can use to prep inventory. The same forecast endpoints power compact
// versions on the Branch Employee + Branch Manager pages.
//
// Endpoints:
//   GET /api/products/forecast/predict?horizon&top_n
//   GET /api/products/forecast/accuracy
//   GET /api/products/forecast/one/{product}?horizon
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./lib/api";
import { mkC } from "./lib/chartFactory";
import { InfoTip } from "./components/InfoTip";

const HORIZONS = [
  { id: "day",   label: "📅 يوم" },
  { id: "week",  label: "🗓 أسبوع" },
  { id: "month", label: "📆 شهر" },
];

const fmt = (n) => Math.round(n || 0).toLocaleString("en-US");

export default function ProductsForecastPanel() {
  const [horizon, setHorizon] = useState("week");
  const [data, setData] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [weather7, setWeather7] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [oneSeries, setOneSeries] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: a }, ev, w] = await Promise.all([
        api.get(`/products/forecast/predict?horizon=${horizon}&top_n=25`),
        api.get(`/products/forecast/accuracy`).catch(() => ({ data: null })),
        api.get(`/products/events/active`).catch(() => ({ data: null })),
        api.get(`/products/weather/forecast?days=7`).catch(() => ({ data: null })),
      ]);
      setData(p);
      setAccuracy(a);
      setActiveEvents(ev?.data?.items || []);
      setWeather7(w?.data?.days || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  // Render distribution chart of top 10 forecasted products
  useEffect(() => {
    if (!data?.items?.length) return;
    const top = data.items.slice(0, 10);
    mkC("c-pf-top", "bar", top.map((p) => p.product.slice(0, 16)), [{
      label: `الكمية المتوقعة (${horizon === "day" ? "اليوم" : horizon === "week" ? "الأسبوع" : "الشهر"})`,
      data: top.map((p) => Math.round(p.total_qty || 0)),
      backgroundColor: top.map((p) => p.confidence >= 75 ? "rgba(8,145,178,.82)"
                                       : p.confidence >= 50 ? "rgba(217,119,6,.82)"
                                       : "rgba(156,163,175,.7)"),
      borderRadius: 6,
    }], { plugins: { legend: { display: false } }, indexAxis: "y" });
  }, [data, horizon]);

  const expandOne = async (product) => {
    setSelected(product);
    setOneSeries(null);
    try {
      const { data } = await api.get(`/products/forecast/one/${encodeURIComponent(product)}?horizon=${horizon === "day" ? "week" : horizon}`);
      setOneSeries(data);
      // Render the per-day line chart for the selected product
      setTimeout(() => {
        if (!data?.daily?.length) return;
        const labels = data.daily.map((d) => `${d.weekday} ${d.date.slice(5)}`);
        mkC("c-pf-one", "line", labels, [
          { label: "الكمية المتوقعة", data: data.daily.map((d) => d.qty),
            borderColor: "#0891B2", backgroundColor: "rgba(8,145,178,.15)",
            fill: true, tension: 0.3, yAxisID: "y" },
          { label: "الإيراد المتوقع (ر.س)", data: data.daily.map((d) => d.rev),
            borderColor: "#7C3AED", backgroundColor: "rgba(124,58,237,.0)",
            fill: false, tension: 0.3, yAxisID: "y1" },
        ], {
          plugins: { legend: { display: true, position: "bottom" } },
          scales: {
            y:  { position: "right", title: { display: true, text: "كمية" } },
            y1: { position: "left", grid: { drawOnChartArea: false }, title: { display: true, text: "إيراد ر.س" } },
          },
        });
      }, 100);
    } catch (_e) { /* ignore */ }
  };

  const items = data?.items || [];
  const acc = accuracy?.accuracy_qty_pct;

  return (
    <div data-testid="products-forecast-panel" className="card" style={{
      padding: 16, marginBottom: 16,
      background: "linear-gradient(135deg, #fff 0%, #ECFEFF 100%)",
      borderInlineStart: "4px solid #0891B2",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0E7490", marginBottom: 4 }}>
            🔮 توقع مبيعات المنتجات
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
            نموذج تنبؤ ذاتي التعلّم يستخدم تقارير المنتجات + موسمية أيام الأسبوع لتوقّع المبيعات القادمة بدقة.
          </div>
        </div>
        {acc !== null && acc !== undefined && (
          <div data-testid="pf-accuracy-badge" style={{
            background: acc >= 80 ? "#DCFCE7" : acc >= 60 ? "#FEF3C7" : "#FEE2E2",
            color: acc >= 80 ? "#16A34A" : acc >= 60 ? "#92400E" : "#DC2626",
            padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 800,
          }}>
            🎯 دقة النموذج: {acc.toFixed(1)}%
            <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2 }}>
              مُقيَّمة على {accuracy?.tested_days || 0} يوم سابق
            </div>
          </div>
        )}
      </div>

      {/* Horizon selector */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {HORIZONS.map((h) => (
          <button key={h.id} onClick={() => setHorizon(h.id)} data-testid={`pf-horizon-${h.id}`}
            style={{
              padding: "7px 14px", borderRadius: 6, fontSize: 12.5, fontWeight: 700,
              cursor: "pointer", border: "1px solid #0891B2",
              background: horizon === h.id ? "#0891B2" : "#fff",
              color: horizon === h.id ? "#fff" : "#0E7490",
            }}>
            {h.label}
          </button>
        ))}
      </div>

      {/* Active context badges — events affecting the forecast right now */}
      {(activeEvents.length > 0 || weather7.length > 0) && (
        <div data-testid="pf-context-strip" style={{
          display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12,
          padding: "8px 10px", background: "#fff", borderRadius: 6,
          border: "1px solid #BAE6FD", fontSize: 11,
        }}>
          <span style={{ fontWeight: 700, color: "#0E7490" }}>سياقات نشطة:</span>
          {activeEvents.map((ev, i) => (
            <span key={`e${i}`} style={{
              padding: "2px 8px", borderRadius: 10, background: "#FEF3C7",
              color: "#92400E", fontWeight: 700,
 }} ><InfoTip text={`${ev.start_date} → ${ev.end_date}`} />
              📅 {ev.name} ×{ev.default_multiplier}
            </span>
          ))}
          {weather7[0] && (
            <span style={{
              padding: "2px 8px", borderRadius: 10,
              background: weather7[0].bucket === "hot" ? "#FEE2E2"
                       : weather7[0].bucket === "cold" ? "#DBEAFE" : "#FEF3C7",
              color:      weather7[0].bucket === "hot" ? "#DC2626"
                       : weather7[0].bucket === "cold" ? "#0891B2" : "#92400E",
              fontWeight: 700,
            }}>
              {weather7[0].bucket === "hot" ? "🔥" : weather7[0].bucket === "cold" ? "❄" : "🌤"} اليوم {weather7[0].temp_max_c}°
            </span>
          )}
          {!activeEvents.length && (
            <span style={{ color: "var(--muted)" }}>لا توجد فعاليات اليوم — التوقع مبني على الموسمية فقط</span>
          )}
        </div>
      )}

      {loading && <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>⏳ جارٍ حساب التنبؤ...</div>}

      {!loading && (!data?.ok || !items.length) && (
        <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", background: "#fff", borderRadius: 6 }}>
          ℹ {data?.message || "ارفع تقريرين على الأقل لتفعيل التنبؤ."}
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          {/* Top KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))",
            gap: 10, marginBottom: 12 }}>
            <KPI label="📅 الفترة" value={
              horizon === "day" ? "يوم واحد" :
              horizon === "week" ? "أسبوع (7 أيام)" : "شهر (30 يوم)"
            } sub={`${data.from} → ${data.to}`} />
            <KPI label="📦 إجمالي الكمية" value={fmt(data.total_qty)} sub={`${items.length} منتج`} />
            <KPI label="💰 إجمالي الإيراد" value={fmt(data.total_rev) + " ر"} />
            <KPI label="📊 مصدر النموذج" value={`${data.snapshots_used} تقرير`}
              sub={data.model_range?.from ? `${data.model_range.from} → ${data.model_range.to}` : ""} />
          </div>

          {/* Top 10 chart */}
          <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid var(--border)", marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0E7490", marginBottom: 8 }}>
              📈 أعلى 10 منتجات متوقّعة
            </div>
            <div style={{ height: 320 }}><canvas id="c-pf-top" data-testid="pf-chart-top"></canvas></div>
          </div>

          {/* Top-N table */}
          <div style={{ background: "#fff", padding: 12, borderRadius: 8, border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0E7490", marginBottom: 8 }}>
              🛒 قائمة التحضير — أعلى {Math.min(25, items.length)} منتج
            </div>
            <div className="tw">
              <table data-testid="pf-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>المنتج</th>
                    <th>الكمية المتوقعة</th>
                    <th>الإيراد المتوقع</th>
                    <th>متوسط/يوم</th>
                    <th>الدقة</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} data-testid={`pf-row-${i}`}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 700 }}>{it.product}</td>
                      <td>{fmt(it.total_qty)}</td>
                      <td>{fmt(it.total_rev)} ر</td>
                      <td>{fmt(it.avg_qty_per_day)}</td>
                      <td>
                        <span
 
                          style={{
                          padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: it.confidence >= 75 ? "#DCFCE7" : it.confidence >= 50 ? "#FEF3C7" : "#FEE2E2",
                          color:      it.confidence >= 75 ? "#16A34A" : it.confidence >= 50 ? "#92400E" : "#DC2626",
                          cursor: "help",
                        }}><InfoTip text={`نسبة الدقة المتوقعة (مستندة على حجم البيانات + تباين الكميات + عدد التقارير). يتحسّن النموذج تلقائياً مع إضافة تقارير أكثر.`} />{it.confidence}%</span>
                      </td>
                      <td>
                        <button onClick={() => expandOne(it.product)} data-testid={`pf-expand-${i}`}
                          style={{ padding: "3px 8px", background: "#0891B2", color: "#fff", border: "none",
                            borderRadius: 4, fontSize: 10.5, cursor: "pointer", fontWeight: 700 }}>
                          📊 تفاصيل
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-product detail */}
          {selected && (
            <div data-testid="pf-detail" style={{ marginTop: 14, background: "#fff", padding: 14,
              borderRadius: 8, border: "1px solid #67E8F9" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0E7490" }}>📊 تفاصيل التنبؤ: {selected}</div>
                <button onClick={() => { setSelected(null); setOneSeries(null); }}
                  style={{ padding: "4px 10px", background: "#fff", color: "#0E7490",
                    border: "1px solid #0891B2", borderRadius: 4, fontSize: 11, cursor: "pointer" }}>
                  إغلاق
                </button>
              </div>
              {!oneSeries && <div style={{ textAlign: "center", color: "var(--muted)", padding: 16 }}>⏳ جارٍ التحميل...</div>}
              {oneSeries && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 8, marginBottom: 10 }}>
                    <Mini label="📦 الكمية" value={fmt(oneSeries.total_qty)} />
                    <Mini label="💰 الإيراد" value={fmt(oneSeries.total_rev) + " ر"} />
                    <Mini label="📅 متوسط/يوم" value={fmt(oneSeries.avg_qty_per_day)} />
                    <Mini label="🎯 الدقة" value={oneSeries.confidence + "%"} />
                    <Mini label="🔄 موسمية المنتج" value={oneSeries.uses_product_seasonality ? "نعم" : "عامة"} />
                  </div>
                  <div style={{ height: 260, marginBottom: 10 }}><canvas id="c-pf-one"></canvas></div>
                  <div className="tw" style={{ maxHeight: 220, overflowY: "auto" }}>
                    <table>
                      <thead><tr><th>التاريخ</th><th>اليوم</th><th>الكمية</th><th>الإيراد</th></tr></thead>
                      <tbody>
                        {(oneSeries.daily || []).map((d, i) => (
                          <tr key={i}>
                            <td>{d.date}</td><td>{d.weekday}</td>
                            <td>{d.qty.toLocaleString("en-US")}</td>
                            <td>{fmt(d.rev)} ر</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div style={{ padding: 10, background: "#fff", borderRadius: 6, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#0E7490", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ padding: 8, background: "#ECFEFF", borderRadius: 4, textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "#0E7490", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#155E75", marginTop: 2 }}>{value}</div>
    </div>
  );
}
